import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import TableComponent from '../../../../components/table/TableComponent';
import HistoryModel from '../../../../components/model/HistoryModel';
import { useAlert } from 'react-alert';
import { useOnMount } from '../../../../components/common/Hooks';
import { setDirection, setPage, setSortField } from '../../../../redux/slices/PaginationSlice';
import { getGroupedHistoryRecords, getHistoryRecords } from './HistoryService';
import './History.css';
import { HistoryFilter } from './HistoryFilter';
import { getEmployees } from '../employees/EmployeesService';
import { isMobile } from 'react-device-detect';
import { ToggleFilterButton } from '../../../../components/buttons/ToggleFilterButton';
import { HistoryGroupedTable } from './HistoryGroupedTable';

const HistoryPage = () => {

    const { t, i18n } = useTranslation();
    const alert = useAlert();
    const onMount = useOnMount();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const pagination = useSelector((state) => state.pagination);
    const [historyRecords, setHistoryRecords] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [employees, setEmployees] = useState([]);
    const actionTypes = ['CREATE', 'UPDATE', 'WRITTEN_OFF', 'DELETED'];
    const [filterValue, setFilterValue] = useState({
        title: null,
        invoice: null,
        employeeId: null,
        type: null,
        from: null,
        till: null,
        date: null,
        groupByDay: false,
    });

    useEffect(() => {
        if (onMount) {
            init();
        } else {
            load();
        }

    }, [pagination.page, pagination.sortByField, pagination.sortingDirection]);

    const init = () => {
        getEmployees()
            .then((response) => setEmployees(response.data))
            .catch(() => alert.error(t('alert:loadingError')));
        if (pagination.sortByField === 'timestamp'
            && pagination.page === 0
            && pagination.sortingDirection === 'DESC') {
            load();
        } else {
            dispatch(setSortField('timestamp'));
            dispatch(setPage(0));
            dispatch(setDirection('DESC'));
        }
    };

    const resolveTable = () => {
        return filterValue.groupByDay
            ? <HistoryGroupedTable content={historyRecords} totalElements={totalElements}></HistoryGroupedTable>
            : <TableComponent
                model={HistoryModel}
                translationsGroup='history:'
                columnsNames={[
                    'productName',
                    'invoice',
                    'amount',
                    'currentAmount',
                    'username',
                    'action',
                    'timestamp',
                    'date',
                ]}
                totalElements={totalElements}
                contents={historyRecords}
            />;
    };

    const load = () => {
        let request = {
            page: pagination.page,
            size: pagination.size,
            ascDesc: pagination.sortingDirection,
            sortField: pagination.sortByField,
            lang: i18n.language.toUpperCase(),
            title: filterValue.title,
            invoice: filterValue.invoice,
            employeeId: filterValue.employeeId,
            type: filterValue.type,
            from: filterValue.from,
            till: filterValue.till,
            date: filterValue.date,
        };
        if (filterValue.groupByDay) {
            getGroupedHistoryRecords(request)
                .then((response) => {
                    setHistoryRecords(response.data.content);
                    setTotalElements(response.data.totalElements);
                })
                .catch(() => alert.error(t('alert:loadingError')))
                .finally(() => setLoading(false));
        } else {
            getHistoryRecords(request)
                .then((response) => {
                    setHistoryRecords(response.data.content.map(record => {
                        return { ...record, action: t('history:' + record.action) };
                    }));
                    setTotalElements(response.data.totalElements);
                })
                .catch(() => alert.error(t('alert:loadingError')))
                .finally(() => setLoading(false));
        }
    };

    return (
        <div>
            <div className={'history-filter'}>
                <div className={isMobile ? 'mobile-filter-container' : 'filter-container'}>
                    {isMobile ? <ToggleFilterButton></ToggleFilterButton> : <></>}
                    <HistoryFilter filter={filterValue}
                                   doFilter={load}
                                   employees={employees}
                                   actionTypes={actionTypes}>
                    </HistoryFilter>
                </div>
            </div>
            {loading
                ? <Spinner animation='border' className='container' />
                : resolveTable()
            }
        </div>
    );
};
export default HistoryPage;
